import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TaskCompletedMeta from './TaskCompletedMeta';
import { useHistory } from 'react-router-dom';
import { Task, deleteInvestment, updateTask } from './api';
import {
  getStartUrlForTask,
  getTaskInfo,
  isLegacyTask,
  TaskReport,
  TaskType,
} from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { User } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import Allocation from './Allocation';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { Program } from '../Reports/NewReportDialog';
import { NPOUser } from '../ReportsBeta/NewProgram/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { AllocationReportCreationProps } from './Allocation/AllocationReportCreation';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { ApolloQueryResult } from 'apollo-boost';
import { submitReport } from '../ReportBuilder/Components/Review/api';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import {
  ReportStage,
  ReportStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';

const getExpectedStageAndStatus = (
  taskType: TaskType,
):
  | {
      status: ReportStatus;
      stage: ReportStage;
    }
  | undefined => {
  if (taskType === 'finalUpdate') {
    return { stage: 'FINAL', status: 'Final Updates Needed' };
  }
  if (taskType === 'interimUpdate') {
    return {
      stage: 'INTERIM',
      status: 'Interim Updates Needed',
    };
  }
  return undefined;
};

interface TaskRowProps {
  task: Task;
  isDebug: boolean;
  buttonText?: string;
  currentUser: User | undefined;
  isSelected?: boolean;
  isLastIndex: boolean;
  allPrograms: RequestState<{
    allPrograms: Program[];
  }>;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  taskType: TaskType;
  taskReport: TaskReport;
  allReports: AllocationReportCreationProps['allReports'];
  reloadTasks: () => unknown;
  reloadPrograms: () =>
    | Promise<
        ApolloQueryResult<{
          allPrograms: Program[];
        }>
      >
    | undefined;
  reloadNPOUser: () => unknown;
}

const selectedStyles = `padding-top: ${SPACERS.medium}; padding-bottom: ${SPACERS.medium}; padding-right: ${SPACERS.medium}; ` +
  `background-color: ${COLORS.ctaPrimaryBackground};`;
export const StyledTaskDiv = styled.div<{
  isSelected: TaskRowProps['isSelected'];
  isOverdue: boolean;
  isCompleted: boolean;
  marginTop?: string;
}>`
  ${({ marginTop }) => marginTop && `margin-top: ${marginTop};`}
  .task-row {
    ${({ isSelected }) =>
      isSelected ? selectedStyles : `padding-bottom: ${SPACERS.tiny};`}
    display: flex;
    align-items: center;
    ${({ isCompleted }) =>
      isCompleted ? '' : 'justify-content: space-between;'}
    gap: ${SPACERS.medium};
    padding-left: ${SPACERS.medium};
    .overdue-symbol {
      color: ${COLORS.critical};
      .icon {
        margin-right: ${SPACERS.extraTiny};
      }
    }
    border-left: 3px solid
      ${({ isOverdue, isCompleted }) =>
        isCompleted
          ? 'transparent'
          : isOverdue
          ? COLORS.critical
          : COLORS.ctaPrimary};
    .task-name {
      margin-bottom: ${SPACERS.extraTiny};
    }
    .task-description {
      margin-bottom: ${SPACERS.tiny};
    }
    .task-meta {
      display: flex;
      gap: ${SPACERS.small};
    }
    .task-row-details {
    }
    .task-open-error {
      margin-top: ${SPACERS.tiny};
    }
    .buttons-wrapper {
      display: flex;
      align-items: center;
      gap: ${SPACERS.tiny};
    }
  }
  .task-row-gap {
    height: ${SPACERS.small};
    border-bottom: 1px solid ${COLORS.grayLight};
  }
`;

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  isDebug,
  buttonText,
  isSelected,
  isLastIndex,
  currentUser,
  allReports,
  allPrograms,
  allNPOUsers,
  taskType,
  taskReport,
  reloadNPOUser,
  reloadPrograms,
  reloadTasks,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requestState, makeRequest] = useGraphQLRequest<any>();
  const [isNewAllocateOn, setIsNewAllocateOn] = useState<boolean>(true);
  const [openAllocationModal, setOpenAllocationModal] = useState<boolean>(
    false,
  );
  const [requestReportUpdateState, makeReportUpdateRequest] = useGraphQLRequest<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >();
  const [error, setError] = useState<string | undefined>(undefined);
  const { push } = useHistory();
  const isOverdue = task.status === 'OVERDUE';
  const isCompleted = task.status === 'COMPLETED';
  const taskInfo = getTaskInfo(task, currentUser);

  const handlePush = useCallback(
    (task: Task) => {
      const start = getStartUrlForTask(task);
      if (start.error !== undefined) {
        setError(start.error);
        return;
      }
      setError(undefined);
      if (start.externalPath) {
        window.open(start.externalPath, '_blank');
      } else if (start.hardRefresh) {
        window.location.href = start.path;
      } else {
        push(start.path);
      }
    },
    [push],
  );

  useEffect(() => {
    if (requestReportUpdateState.type !== 'REQUEST_SUCCESS') {
      return;
    }
    handlePush(task);
  }, [requestReportUpdateState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.altKey &&
        event.code === 'KeyA'
      ) {
        setIsNewAllocateOn(o => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setIsNewAllocateOn]);

  const handleOpenAllocationModal = useCallback(() => {
    setOpenAllocationModal(true);
  }, [setOpenAllocationModal]);
  const handleCloseAllocationModal = useCallback(() => {
    setOpenAllocationModal(false);
  }, [setOpenAllocationModal]);

  const handleComplete = useCallback(() => {
    if (!client) return;
    if (task.taskType === 'ALLOCATE') {
      const investmentId = task.invitation?.investment?.dbId;
      if (!investmentId) return;
      makeRequest(deleteInvestment(client, { id: investmentId }));
      return;
    }
    makeRequest(
      updateTask(client, {
        id: task.taskId,
        status: isCompleted ? 'UNCOMPLETED' : 'COMPLETED',
      }),
    );
  }, [client, task, isCompleted, makeRequest]);

  const handleStart = useCallback(() => {
    if (isNewAllocateOn && task.taskType === 'ALLOCATE') {
      handleOpenAllocationModal();
      return;
    }
    const report = taskReport[task.taskId];
    const expectedStageAndStatus = getExpectedStageAndStatus(taskType);
    if (expectedStageAndStatus && client && report) {
      if (
        report.report.stage !== expectedStageAndStatus.stage ||
        report.report.status !== expectedStageAndStatus.status
      ) {
        const request = submitReport(
          client,
          report.report.dbId,
          expectedStageAndStatus.status,
          expectedStageAndStatus.stage,
        );
        request.then(() => {
          const reportData =
            reportState.type === 'REQUEST_SUCCESS'
              ? reportState.data
              : undefined;
          trackEvent('ReportStatusChanged', {
            ...(reportData
              ? pullReportData(
                  reportData,
                  isReportEndDatePassed,
                  userStaticType,
                )
              : {}),
            reportId: report.report.dbId,
            previousStatus: report.report.status,
            previousStage: report.report.stage,
            status: expectedStageAndStatus.status,
            stage: expectedStageAndStatus.stage,
          });
        });
        makeReportUpdateRequest(request);
        return;
      }
    }
    handlePush(task);
  }, [
    client,
    handleOpenAllocationModal,
    handlePush,
    isNewAllocateOn,
    makeReportUpdateRequest,
    task,
    taskReport,
    taskType,
  ]);

  return (
    <StyledTaskDiv
      isCompleted={isCompleted}
      isSelected={isSelected}
      isOverdue={isOverdue}
      marginTop={SPACERS.medium}
    >
      <div className="task-row">
        {isCompleted && (
          <FontAwesomeIcon icon={faCheck} color={COLORS.success} />
        )}
        <div className="task-row-details">
          <TrueImpactText className="task-name" variant="body-large-bold">
            {taskInfo.name}
          </TrueImpactText>
          <TrueImpactText className="task-description">
            {taskInfo.description}
            {task.allocation?.report.year && (
              <> &mdash; report year {task.allocation?.report.year}</>
            )}
          </TrueImpactText>
          {task.completionDate ? (
            <TaskCompletedMeta
              task={task}
              completionDate={task.completionDate}
              currentUser={currentUser}
              className="task-meta"
            />
          ) : (
            taskInfo.meta
          )}
          {error && (
            <TrueImpactText
              variant="body-small"
              color="critical"
              className="task-open-error"
            >
              {error + ' Please contact us if the error persist.'}
            </TrueImpactText>
          )}
        </div>
        <div className="buttons-wrapper">
          {isDebug && taskType !== 'finalUpdate' && taskType !== 'feedback' && (
            <TrueImpactButton
              variant="secondary-large"
              onClick={handleComplete}
              disabled={requestState.type !== 'REQUEST_INIT'}
              isLoading={requestState.type === 'REQUEST_START'}
            >
              {requestState.type === 'REQUEST_SUCCESS' ? (
                <FontAwesomeIcon color={COLORS.success} icon={faCheck} />
              ) : requestState.type === 'REQUEST_ERROR' ? (
                <FontAwesomeIcon color={COLORS.critical} icon={faXmark} />
              ) : (
                <>Marks as {isCompleted ? 'incomplete' : 'completed'}</>
              )}
            </TrueImpactButton>
          )}
          {!isCompleted && (
            <TrueImpactButton
              onClick={handleStart}
              isLoading={requestReportUpdateState.type === 'REQUEST_START'}
            >
              {isNewAllocateOn && task.taskType === 'ALLOCATE' ? (
                allPrograms.type === 'REQUEST_START' ||
                allNPOUsers.type === 'REQUEST_START' ? (
                  <TrueImpactLoading
                    singleLine
                    marginTop={0}
                    marginBottom={0}
                  />
                ) : (
                  'Allocate'
                )
              ) : (
                buttonText || 'Start'
              )}
            </TrueImpactButton>
          )}
        </div>
      </div>
      {!isSelected && !isLastIndex && <div className="task-row-gap"></div>}
      {openAllocationModal && (
        <Allocation
          task={task}
          open={true}
          allReports={allReports}
          allPrograms={allPrograms}
          allNPOUsers={allNPOUsers}
          handleClose={handleCloseAllocationModal}
          reloadTasks={reloadTasks}
          reloadPrograms={reloadPrograms}
          reloadNPOUser={reloadNPOUser}
        />
      )}
    </StyledTaskDiv>
  );
};

export default TaskRow;
